import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiTrash2, FiInfo } = FiIcons;

const PAYMENT_SCHEDULES = [
  { value: 'on_release', label: 'On Release' },
  { value: 'weekly',     label: 'Weekly' },
  { value: 'monthly',    label: 'Monthly' },
  { value: 'quarterly',  label: 'Quarterly' },
  { value: 'milestone',  label: 'Per Milestone' },
];

const EXCLUSIVITY_OPTS = [
  { value: 'exclusive',     label: 'Exclusive',     desc: 'Cannot work with competing artists' },
  { value: 'non_exclusive', label: 'Non-Exclusive',  desc: 'Free to collaborate with others' },
  { value: 'genre_locked',  label: 'Genre-Locked',   desc: 'Exclusive within genre only' },
];

const TERRITORIES = [
  { value: 'worldwide',      label: 'Worldwide' },
  { value: 'north_america',  label: 'North America' },
  { value: 'europe',         label: 'Europe' },
  { value: 'asia_pacific',   label: 'Asia Pacific' },
  { value: 'latin_america',  label: 'Latin America' },
  { value: 'custom',         label: 'Custom' },
];

const CONTENT_RIGHTS = [
  { value: 'shared',          label: 'Shared Rights',    desc: 'Both parties co-own the content' },
  { value: 'initiator_owns',  label: 'Initiator Owns',   desc: 'Initiating artist retains rights' },
  { value: 'partner_owns',    label: 'Partner Owns',     desc: 'Partner artist retains rights' },
  { value: 'licensed',        label: 'Licensed Use',     desc: 'Rights licensed, not transferred' },
];

const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors";
const Field = ({ label, tip, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</label>
      {tip && (
        <span className="group relative">
          <SafeIcon icon={FiInfo} className="text-zinc-700 text-xs cursor-help" />
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-zinc-800 border border-zinc-700 rounded-lg text-[10px] font-bold text-zinc-300 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {tip}
          </span>
        </span>
      )}
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, active, onClick }) => (
  <button type="button" onClick={onClick}
    className={`w-full flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all ${
      active ? 'border-indigo-500/40 bg-indigo-500/8' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'
    }`}>
    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
      active ? 'border-indigo-400 bg-indigo-400' : 'border-zinc-600'
    }`}>
      {active && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
    </div>
    <div>
      <p className={`text-xs font-black uppercase tracking-tight ${active ? 'text-white' : 'text-zinc-400'}`}>{label}</p>
      {desc && <p className="text-[10px] font-bold text-zinc-600 mt-0.5">{desc}</p>}
    </div>
  </button>
);

const TermsEditor = ({ terms, onChange, showRevSplit, revSplit, onRevSplitChange }) => {
  const [newClause, setNewClause] = useState('');

  const set = (k, v) => onChange({ ...terms, [k]: v });

  const addClause = () => {
    if (!newClause.trim()) return;
    set('custom_clauses', [...(terms.custom_clauses || []), newClause.trim()]);
    setNewClause('');
  };

  const removeClause = (i) =>
    set('custom_clauses', terms.custom_clauses.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-7">
      {/* Revenue Split */}
      {showRevSplit && (
        <Field label={`Revenue Split — Your cut: ${revSplit}%`} tip="Percentage of revenue going to the initiating artist">
          <input type="range" min="0" max="100" value={revSplit}
            onChange={e => onRevSplitChange(Number(e.target.value))}
            className="w-full accent-indigo-400" />
          <div className="flex justify-between text-[10px] font-black text-zinc-600 uppercase tracking-widest mt-1">
            <span>0% (Partner)</span><span>50 / 50</span><span>100% (You)</span>
          </div>
          <div className="mt-2 flex gap-2">
            <div className="flex-1 h-2 rounded-full bg-indigo-500/80" style={{ width: `${revSplit}%` }} />
            <div className="flex-1 h-2 rounded-full bg-zinc-700" style={{ width: `${100 - revSplit}%` }} />
          </div>
          <div className="flex justify-between text-[10px] font-black mt-1">
            <span className="text-indigo-400">You: {revSplit}%</span>
            <span className="text-zinc-500">Partner: {100 - revSplit}%</span>
          </div>
        </Field>
      )}

      {/* Payment Schedule */}
      <Field label="Payment Schedule" tip="How often revenue is distributed between partners">
        <div className="flex flex-wrap gap-2">
          {PAYMENT_SCHEDULES.map(opt => (
            <button key={opt.value} type="button" onClick={() => set('payment_schedule', opt.value)}
              className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                terms.payment_schedule === opt.value
                  ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300'
                  : 'border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </Field>

      {/* Exclusivity */}
      <Field label="Exclusivity" tip="Defines how exclusive this partnership is">
        <div className="space-y-2">
          {EXCLUSIVITY_OPTS.map(opt => (
            <ToggleRow key={opt.value} label={opt.label} desc={opt.desc}
              active={terms.exclusivity === opt.value}
              onClick={() => set('exclusivity', opt.value)} />
          ))}
        </div>
      </Field>

      {/* Territory */}
      <Field label="Territory" tip="Geographic scope of this partnership">
        <div className="grid grid-cols-2 gap-2">
          {TERRITORIES.map(opt => (
            <button key={opt.value} type="button" onClick={() => set('territory', opt.value)}
              className={`px-3 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-widest text-left transition-all ${
                terms.territory === opt.value
                  ? 'bg-white text-black border-white'
                  : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </Field>

      {/* Content Rights */}
      <Field label="Content Rights" tip="Who owns the content created under this partnership">
        <div className="space-y-2">
          {CONTENT_RIGHTS.map(opt => (
            <ToggleRow key={opt.value} label={opt.label} desc={opt.desc}
              active={terms.content_rights === opt.value}
              onClick={() => set('content_rights', opt.value)} />
          ))}
        </div>
      </Field>

      {/* Cancellation Notice */}
      <Field label={`Cancellation Notice — ${terms.cancellation_notice_days} days`} tip="Days notice required before ending the partnership">
        <input type="range" min="7" max="180" step="7" value={terms.cancellation_notice_days}
          onChange={e => set('cancellation_notice_days', Number(e.target.value))}
          className="w-full accent-indigo-400" />
        <div className="flex justify-between text-[10px] font-black text-zinc-600 uppercase tracking-widest mt-1">
          <span>7 days</span><span>90 days</span><span>180 days</span>
        </div>
      </Field>

      {/* Auto-Renew */}
      <Field label="Auto-Renew" tip="Automatically renew this partnership when it expires">
        <div className="flex items-center gap-4">
          {[true, false].map(val => (
            <button key={String(val)} type="button" onClick={() => set('auto_renew', val)}
              className={`flex-1 py-3 rounded-xl border text-xs font-black uppercase tracking-widest transition-all ${
                terms.auto_renew === val
                  ? val ? 'bg-green-500/10 border-green-500/40 text-green-400' : 'bg-red-500/10 border-red-500/40 text-red-400'
                  : 'border-zinc-800 text-zinc-600 hover:border-zinc-700'
              }`}>
              {val ? 'Yes — Auto-Renew' : 'No — Manual Renewal'}
            </button>
          ))}
        </div>
      </Field>

      {/* Custom Clauses */}
      <Field label="Custom Clauses" tip="Add any specific terms or conditions unique to this deal">
        <div className="space-y-2">
          {(terms.custom_clauses || []).map((clause, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-zinc-900 border border-zinc-800 rounded-xl">
              <p className="flex-1 text-xs font-bold text-zinc-300 leading-relaxed">{clause}</p>
              <button type="button" onClick={() => removeClause(i)}
                className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0">
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input className={`${inputCls} flex-1`} placeholder="Add a custom clause..."
              value={newClause} onChange={e => setNewClause(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addClause())} />
            <button type="button" onClick={addClause}
              className="px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-zinc-300 hover:text-white hover:border-zinc-600 transition-all">
              <SafeIcon icon={FiPlus} className="text-sm" />
            </button>
          </div>
        </div>
      </Field>
    </div>
  );
};

export default TermsEditor;